import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteChrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function Shell({ children }) {
  return (
    <div className="min-h-screen">
      <SiteHeader links={false} />
      <main className="max-w-md mx-auto px-4 py-14 sm:py-20">{children}</main>
    </div>
  );
}

export function Login() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = (u) => { setUser(u); nav(u.role === "admin" ? "/admin" : !u.has_password ? "/account/setup" : params.get("next") || "/dashboard"); };
  const verifyCode = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/verify-device", { challenge_id: challenge.challenge_id, code }); done(data.user); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const magic = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/magic-link", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password: pw });
      if (data.requires_device_verification) { setChallenge(data); return; }
      done(data.user);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (challenge) {
    return (
      <Shell>
        <form onSubmit={verifyCode} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="device-verification-form">
          <ShieldCheck className="h-7 w-7 text-teal-700" />
          <h1 className="text-2xl font-bold text-slate-900">Confirm it's you</h1>
          <p className="text-sm text-slate-600">This is a new device for your account. We've emailed a 6-digit code to <strong>{challenge.email_hint}</strong>. It expires in 10 minutes.</p>
          <div className="space-y-2"><Label htmlFor="dev-code">Verification code</Label><Input id="dev-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-lg tracking-[0.4em]" data-testid="device-code-input" /></div>
          <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-full" data-testid="device-code-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and sign in"}</Button>
          <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="text-xs text-slate-500 underline" data-testid="device-code-back">Back to sign in</button>
        </form>
      </Shell>
    );
  }
  return (
    <Shell>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">Sign in or restore access</h1>
      <p className="text-slate-600 text-sm mb-6">Use the email you purchased with.</p>
      {params.get("reason") === "no-access" && <p className="mb-5 text-sm rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-900" data-testid="no-access-notice">This account doesn't have active course access. If you purchased, request a link with your purchase email, or <Link to="/" className="underline">buy the course</Link>.</p>}
      <Tabs defaultValue="link" className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <TabsList className="grid grid-cols-2 w-full mb-5">
          <TabsTrigger value="link" data-testid="login-tab-link"><Mail className="h-4 w-4 mr-1.5" />Email link</TabsTrigger>
          <TabsTrigger value="password" data-testid="login-tab-password"><KeyRound className="h-4 w-4 mr-1.5" />Password</TabsTrigger>
        </TabsList>
        <TabsContent value="link">
          {sent ? (
            <div className="text-sm text-slate-700 space-y-2" data-testid="magic-link-sent">
              <CheckCircle2 className="h-6 w-6 text-teal-600" />
              <p>If <strong>{email}</strong> has course access, a sign-in link is on its way. It expires in 30 minutes. Check your spam folder too.</p>
            </div>
          ) : (
            <form onSubmit={magic} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="ml-email">Purchase email</Label><Input id="ml-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="magic-link-email-input" /></div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="magic-link-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a sign-in link"}</Button>
            </form>
          )}
        </TabsContent>
        <TabsContent value="password">
          <form onSubmit={login} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="pw-email">Email</Label><Input id="pw-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></div>
            <div className="space-y-2"><Label htmlFor="pw-pass">Password</Label><Input id="pw-pass" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="login-password-input" /></div>
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="login-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
            <p className="text-xs text-slate-500">No password yet? Use the email link, then set one from your account menu.</p>
          </form>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

export function AuthVerify() {
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    api.post("/auth/verify", { token: params.get("token") || "" })
      .then(({ data }) => { setUser(data.user); nav(data.user.role === "admin" ? "/admin" : !data.user.has_password ? "/account/setup" : "/dashboard", { replace: true }); })
      .catch((e) => setError(errMsg(e)));
  }, [params, nav, setUser]);
  return (
    <Shell>
      {error ? (
        <div className="space-y-4 text-center" data-testid="verify-error"><XCircle className="h-8 w-8 text-rose-600 mx-auto" /><p className="text-slate-700">{error}</p><Button asChild className="rounded-full"><Link to="/login">Request a new link</Link></Button></div>
      ) : <div className="text-center text-slate-600 flex flex-col items-center gap-3" data-testid="verify-loading"><Loader2 className="h-7 w-7 animate-spin text-teal-700" />Signing you in…</div>}
    </Shell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const { setUser } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0, stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          const c = await api.post(`/payments/claim/${sid}`).catch(() => null);
          if (c) { setUser(c.data.user); setTimeout(() => nav(c.data.user.has_password ? "/dashboard" : "/account/setup", { replace: true }), 1200); } else setState("paid-email");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries < 20) setTimeout(tick, 2000); else setState("timeout");
    };
    tick();
    return () => { stop = true; };
  }, [params, nav, setUser]);
  const msg = {
    checking: ["Confirming your payment…", "This usually takes a few seconds."],
    paid: ["Payment confirmed. Welcome!", "Next, create your account…"],
    "paid-email": ["Payment confirmed.", "We've emailed you a link to create your account. If you already created it, sign in."],
    failed: ["Payment didn't complete.", "No charge was made. You can try again from the course page."],
    timeout: ["Still confirming…", "Your payment may still be processing. Check your email for a sign-in link, or restore access in a few minutes."],
    error: ["Missing payment reference.", "Return to the course page and try again."],
  }[state];
  return (
    <Shell>
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-3 shadow-sm" data-testid={`payment-status-${state}`}>
        {state === "checking" || state === "paid" ? <Loader2 className="h-8 w-8 animate-spin text-teal-700 mx-auto" /> : state === "failed" || state === "error" ? <XCircle className="h-8 w-8 text-rose-600 mx-auto" /> : <CheckCircle2 className="h-8 w-8 text-teal-600 mx-auto" />}
        <h1 className="text-2xl font-bold text-slate-900">{msg[0]}</h1>
        <p className="text-slate-600 text-sm">{msg[1]}</p>
        {["failed", "error", "timeout", "paid-email"].includes(state) && <div className="flex gap-3 justify-center pt-2"><Button asChild variant="outline" className="rounded-full"><Link to="/">Course page</Link></Button><Button asChild className="rounded-full"><Link to="/login">Restore access</Link></Button></div>}
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-3 shadow-sm" data-testid="payment-cancelled">
        <h1 className="text-2xl font-bold text-slate-900">Checkout cancelled</h1>
        <p className="text-slate-600 text-sm">No payment was taken. You can come back whenever you're ready.</p>
        <Button asChild className="rounded-full"><Link to="/" data-testid="payment-cancel-back">Back to the course page</Link></Button>
      </div>
    </Shell>
  );
}

export function CreateAccount() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { password: pw, name });
      const { data } = await api.get("/auth/me");
      setUser(data); toast.success("Account created"); nav("/dashboard", { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">Create your account</h1>
      <p className="text-slate-600 text-sm mb-6">Your account uses your purchase email. You'll sign in with it and this password.</p>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4" data-testid="create-account-form">
        <div className="space-y-2"><Label>Email</Label><Input value={user?.email || ""} disabled data-testid="create-account-email" /></div>
        <div className="space-y-2"><Label htmlFor="ca-name">Your name</Label><Input id="ca-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} data-testid="create-account-name" /></div>
        <div className="space-y-2"><Label htmlFor="ca-pw">Password (min 8 characters)</Label><Input id="ca-pw" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="create-account-password" /></div>
        <div className="space-y-2"><Label htmlFor="ca-pw2">Confirm password</Label><Input id="ca-pw2" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="create-account-password-confirm" /></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="create-account-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
        <p className="text-xs text-slate-500">For your security, the course is linked to this email and this device. Signing in on a new device needs a code sent to your email. Accounts can't be shared.</p>
      </form>
    </Shell>
  );
}
